<template>
  <toast ref="toast" />

  <h2 class="container my-4">
    {{$t("user_settings.title_edit")}}
  </h2>

  <template  v-if="dispNotFound && userId !== null">
    <section class="container my-4 p-5 bg-light text-center">
      <span>{{$t("errors.not_found")}}</span>
    </section>

    <section class="container my-4 d-flex">
      <a v-on:click="onPrevClick" class="btn btn-outline-success me-auto float-start">
        {{$t("buttons.return")}}
      </a>
    </section>
  </template>

  <template v-else>
    <section v-if="summaryErrors.length"
             class="container my-4">
      <validation-errors class="alert alert-danger pb-0 overflow-hidden"
                         :errors="summaryErrors" />
    </section>

    <section class="container my-4 d-flex">
      <a v-on:click="onPrevClick" class="btn btn-outline-success me-auto float-start">
        {{$t("buttons.return")}}
      </a>
      <a v-on:click="onUpdateClick" class="btn btn btn-outline-primary ms-2">
        {{$t("buttons.update")}}
      </a>
    </section>

    <section class="container my-4">
      <h4>{{$t("items.login_code")}}</h4>
      <p>{{user.loginCode}}</p>
    </section>

    <basic-input ref="basic-input" type="text"
                 class="container my-4"
                 v-model="user.name"
                 :attributes="attributes.Name" />

    <basic-input ref="basic-input" type="email"
                 class="container my-4"
                 v-model="user.email"
                 :attributes="attributes.Email" />

    <div class="container my-4">
      <h4>
        {{$t("user_settings.user_photo")}}
      </h4>
      <p class="form-control-plaintext"><img class="user__userphoto-icon" :src="imageUrl" /></p>
      <a href="#" v-if="user.hasPhoto && user.userPhoto.fileKey !== null" v-on:click.prevent="onPhotoDeleteClick($event)">{{$t("buttons.delete")}}</a>
      <dropzone-input idPrefix="user-photo-uploader"
                      url="/api/temporaryuserphoto"
                      :fileCountMessageVisible="true"
                      :createImageThumbnails="true"
                      :thumbnailsVisible="false"
                      :thumbnailWidth="300"
                      :thumbnailHeight="300"
                      :maxFilesize="1024"
                      acceptedFiles="image/*"
                      maxFilesExceededAction="pushout"
                      v-on:success="userPhotoUploadSuccess"
                      v-on:removedfile="removeUserPhotoFile"
                      v-on:thumbnail="thumbnailsCreated" />
    </div>

    <section class="container my-4 d-flex">
      <a v-on:click="onPrevClick" class="btn btn-outline-success me-auto float-start">
        {{$t("buttons.return")}}
      </a>
      <a v-on:click="onUpdateClick" class="btn btn btn-outline-primary ms-2">
        {{$t("buttons.update")}}
      </a>
    </section>
  </template>
</template>

<script>
  import { defineComponent } from "vue";
  import { xhrRequest, xhrErrors, xhrSummaryErrors } from "utils/xhr";
  import Spinner from "utils/spinner";
  import ValidationErrors from "components/base/validation_errors";
  import AlertToast from "components/toast/alert_toast";
  import BasicInput from "components/input/basic_input";
  import DropzoneInput from "components/input/dropzone_input";

  const computed = {
    imageUrl() {
      return this.dropzoneThumbnails ?? this.user.userPhoto.photoUrl;
    },
  };

  const methods = {
    loadRecord() {
      if (this.loading) {
        return;
      }
      this.loading = true;
      let spinner = new Spinner();
      xhrRequest(
        "GET",
        "users/",
        null,
        {
          success: data => {
            this.user = data;

            // ユーザー画像取得
            this.getUserPhoto();
          },
          error: (xhr, status, error) => {
            this.errors = xhrErrors(xhr);
            this.summaryErrors = xhrSummaryErrors(xhr);
            this.dispNotFound = true;
          },
          complete: () => {
            this.scrollTop();
            spinner.stop();
            this.loading = false;
          }
        }
      );
    },
    getUserPhoto() {
      let spinner = new Spinner();
      xhrRequest(
        "GET",
        "userphotos/",
        { userId: this.userId },
        {
          success: data => {
            this.user.userPhoto = data;
          },
          error: (xhr, status, error) => {
            this.errors = xhrErrors(xhr);
            this.summaryErrors = xhrSummaryErrors(xhr);
          },
          complete: () => {
            this.scrollTop();
            spinner.stop();
            this.loading = false;
          }
        }
      );
    },
    getDefaultUserPhoto() {
      let spinner = new Spinner();
      xhrRequest(
        "GET",
        "userphotos/GetDefaultUserPhoto",
        this.userId,
        {
          success: data => {
            this.defaultPhotoUrl = data.url;
          },
          error: (xhr, status, error) => {
            this.errors = xhrErrors(xhr);
            this.summaryErrors = xhrSummaryErrors(xhr);
          },
          complete: () => {
            this.scrollTop();
            spinner.stop();
          }
        }
      );
    },
    getAttributes() {
      const spinner = new Spinner();
      xhrRequest(
        "GET",
        "viewmodels/UserViewModel",
        null,
        {
          success: data => {
            this.attributes = data;
          },
          error: (xhr, status, error) => {
            this.errors = xhrErrors(xhr);
            this.summaryErrors = xhrSummaryErrors(xhr);
          },
          complete: () => {
            spinner.stop();
            this.loading = false;
          }
        }
      );
    },
    scrollTop: () => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    },
    onPrevClick() {
      // ToDo: 一般ユーザー向けメニュー画面への遷移
      window.location.href = "";
    },
    onUpdateClick() {
      this.validate();
    },
    validate() {
      if (this.loading) {
        return false;
      }
      this.loading = true;
      const spinner = new Spinner();
      xhrRequest(
        "POST",
        "users/validate",
        this.user,
        {
          success: data => {
            this.errors = {};
            this.summaryErrors = {};

            let confirmResult = true;

            if (this.userId !== null) {
              confirmResult = confirm(this.$t("messages.update_confirm"))
            }

            if (confirmResult) {
              this.update();
            }
          },
          error: (xhr, status, error) => {
            this.errors = xhrErrors(xhr);
            this.summaryErrors = xhrSummaryErrors(xhr);
          },
          complete: () => {
            spinner.stop();
            this.loading = false;
          }
        }
      );
    },
    update() {
      this.loading = true;
      let spinner = new Spinner();
      xhrRequest(
        "PUT",
        "users/",
        this.user,
        {
          success: data => {
            // 通知を表示
            this.$refs.toast.storeToast({
              title: "",
              body: this.$t("messages.operation_completed", { operation: this.$t("buttons.update") }),
              isSimpleLayout: true,
            });

            // ToDo: 一般ユーザー向けメニュー画面への遷移
            window.location.href = "";
          },
          error: (xhr, status, error) => {
            this.errors = xhrErrors(xhr);
            this.summaryErrors = xhrSummaryErrors(xhr);
          },
          complete: () => {
            spinner.stop();
            this.loading = false;
          }
        }
      );
    },
    userPhotoUploadSuccess(file) {
      let uploadData = JSON.parse(file.xhr.responseText);
      this.user.userPhoto.temporaryId = uploadData.id;
      this.user.userPhoto.fileKey = uploadData.fileKey;
      this.user.userPhoto.fileName = uploadData.fileName;
      this.user.userPhoto.fileSize = uploadData.fileSize;
      this.user.userPhoto.fileType = uploadData.fileType;
    },
    removeUserPhotoFile(file) {
      let removedData = JSON.parse(file.xhr.responseText);
      this.deleteUserPhotoFile(removedData.id);
      this.dropzoneThumbnails = null;
      this.user.userPhoto.photoUrl = this.defaultPhotoUrl;
    },
    deleteUserPhotoFile(id) {
      if (this.loading) {
        return;
      }
      this.loading = true;
      let spinner = new Spinner();
      xhrRequest(
        "DELETE",
        `temporaryuserphoto/${id}`,
        null,
        {
          success: data => { },
          error: (xhr, status, error) => { },
          complete: () => {
            spinner.stop();
            this.loading = false;
          }
        }
      );
    },
    onPhotoDeleteClick() {
      this.user.userPhoto.fileKey = null;
      this.user.userPhoto.photoUrl = this.defaultPhotoUrl;
    },
    thumbnailsCreated(data) {
      this.dropzoneThumbnails = data.dataUrl;
    }
  };

  const onInit = () => { };

  const onMounted = function () {
    this.getAttributes();
    this.loadRecord();

    // デフォルトユーザー画像を取得
    this.getDefaultUserPhoto();
  };

  export default defineComponent({
    data: () => {
      return {
        user: {
          id: 0,
          loginCode: "",
          name: "",
          email: "",
          userPhoto: {
            id: 0,
            temporaryId: 0,
            fileKey: "",
            fileName: "",
            fileSize: 0,
            fileType: "",
            photoUrl: "",
          },
        },
        attributes: [],
        errors: {},
        summaryErrors: {},
        loading: false,
        defaultPhotoUrl: "",
        dropzoneThumbnails: null,
        dispNotFound:false,
      };
    },
    created: onInit,
    mounted: onMounted,
    methods: methods,
    computed: computed,
    components: {
      "validation-errors": ValidationErrors,
      "toast": AlertToast,
      "basic-input": BasicInput,
      "dropzone-input": DropzoneInput,
    }
  });
</script>

<style scoped>
  .user__userphoto-icon {
    height: 200px;
    width: 200px;
  }
</style>
